// src/hooks/useNoteFormat.ts
import { useEffect, useState } from "react";
import { useAuth } from "@/shared/contexts/AuthContext";
import { useVertical } from "@/shared/contexts/VerticalContext";
import { supabase } from "@/shared/lib/supabase";
import { resolveNoteFormat } from "@/shared/lib/noteFormat";
import {
  NOTE_FORMATS,
  type NoteFormatDefinition,
} from "@/shared/lib/noteFormats";

// Module-level cache so multiple consumers don't refetch the profile.
// Keyed by user id; value is the user's preferred_note_format string (or null).
const profileFormatCache = new Map<string, string | null>();

export function useNoteFormat(): NoteFormatDefinition {
  const { user } = useAuth();
  const { verticalConfig } = useVertical();
  const [profilePreferred, setProfilePreferred] = useState<string | null>(
    user ? (profileFormatCache.get(user.id) ?? null) : null,
  );

  useEffect(() => {
    if (!user) return;
    if (profileFormatCache.has(user.id)) {
      setProfilePreferred(profileFormatCache.get(user.id) ?? null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return;
        const res = await fetch("/api/onboarding", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = (await res.json()) as {
          preferred_note_format: string | null;
        };
        profileFormatCache.set(user.id, data.preferred_note_format ?? null);
        if (!cancelled) setProfilePreferred(data.preferred_note_format ?? null);
      } catch {
        /* leave preference as null; resolver falls back to vertical default */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    resolveNoteFormat(profilePreferred, verticalConfig.defaultNoteFormat) ??
    NOTE_FORMATS.freeform
  );
}

/** Test/debug helper — clear the cache when profile is updated externally. */
export function invalidateNoteFormatCache(userId?: string): void {
  if (userId) profileFormatCache.delete(userId);
  else profileFormatCache.clear();
}
